import resourcesAvailabilityService from '../services/resourcesAvailabilityService';

const state = {
  availabilitySlots: [],
  isLoadingAvailability: false,
  hasLoadedAvailability: false,
  lastParams: {},
  error: null,
  lastUpdated: null,
};

const getters = {
  availabilitySlots: (state) => state.availabilitySlots,
  isLoadingAvailability: (state) => state.isLoadingAvailability,
  hasLoadedAvailability: (state) => state.hasLoadedAvailability,
  error: (state) => state.error,
  lastParams: (state) => state.lastParams,
  lastUpdated: (state) => state.lastUpdated,
  
  getSlotsByResourceUid: (state) => (resourceUid) => 
    state.availabilitySlots.filter(slot => slot.resource_uid === resourceUid),
  
  getSlotsByStaffUid: (state) => (staffUid) => 
    state.availabilitySlots.filter(slot => slot.staff_uid === staffUid),
  
  getSlotsByDate: (state) => (date) => 
    state.availabilitySlots.filter(slot => {
      if (!slot.start_time) return false;
      const slotDate = new Date(slot.start_time).toISOString().split('T')[0];
      return slotDate === date;
    }),
  
  getSlotsInRange: (state) => (startDate, endDate) => 
    state.availabilitySlots.filter(slot => {
      if (!slot.start_time) return false;
      const slotDateTime = new Date(slot.start_time);
      const start = new Date(startDate);
      const end = new Date(endDate);
      return slotDateTime >= start && slotDateTime <= end;
    }),
  
  hasDataForParams: (state) => (params) => {
    if (!state.hasLoadedAvailability || !state.lastParams) return false;
    
    const lastParams = state.lastParams;
    return (
      JSON.stringify(params.resource_uids) === JSON.stringify(lastParams.resource_uids) &&
      JSON.stringify(params.staff_uids) === JSON.stringify(lastParams.staff_uids) &&
      JSON.stringify(params.resource_type_uids) === JSON.stringify(lastParams.resource_type_uids) &&
      params.service_uid === lastParams.service_uid &&
      params.slot_duration === lastParams.slot_duration &&
      params.start_time === lastParams.start_time &&
      params.end_time === lastParams.end_time
    );
  },
};

const mutations = {
  setAvailabilitySlots(state, slots) {
    state.availabilitySlots = slots || [];
  },
  
  mergeAvailabilitySlots(state, newSlots) {
    if (!newSlots || newSlots.length === 0) {
      return;
    }
    
    // Create a map of existing slots by start time for quick lookup
    const existingSlotsMap = new Map();
    state.availabilitySlots.forEach(slot => {
      if (slot.start) {
        existingSlotsMap.set(slot.start, slot);
      }
    });
    
    // Merge new slots, replacing duplicates
    newSlots.forEach(slot => {
      if (slot.start) {
        existingSlotsMap.set(slot.start, slot);
      }
    });
    
    // Convert back to array
    state.availabilitySlots = Array.from(existingSlotsMap.values());
  },
  
  setLoadingAvailability(state, isLoading) {
    state.isLoadingAvailability = isLoading;
  },
  
  setHasLoadedAvailability(state, hasLoaded) {
    state.hasLoadedAvailability = hasLoaded;
  },
  
  setLastParams(state, params) {
    state.lastParams = { ...params };
  },
  
  setError(state, error) {
    state.error = error;
  },
  
  setLastUpdated(state, timestamp) {
    state.lastUpdated = timestamp;
  },
  
  resetAvailabilityData(state) {
    state.availabilitySlots = [];
    state.hasLoadedAvailability = false;
    state.isLoadingAvailability = false;
    state.lastParams = {};
    state.error = null;
    state.lastUpdated = null;
  },
  
  clearError(state) {
    state.error = null;
  },
};

const actions = {
  async fetchAvailabilitySlots({ commit, state }, params = {}) {
    if (state.isLoadingAvailability) {
      return [];
    }
    
    commit('setLoadingAvailability', true);
    commit('clearError');
    
    try {
      const slots = await resourcesAvailabilityService.getAvailabilitySlots(params);
      // Ensure slots is an array
      const slotsArray = Array.isArray(slots) ? slots : [];
      // Store the fetched slots temporarily (will be merged by loadAvailabilitySlots)
      commit('setAvailabilitySlots', slotsArray);
      commit('setHasLoadedAvailability', true);
      commit('setLastParams', params);
      commit('setLastUpdated', new Date().toISOString());
      return slotsArray;
    } catch (error) {
      console.error('Error fetching availability slots:', error);
      commit('setError', error.message || 'Failed to fetch availability slots');
      commit('setAvailabilitySlots', []);
      throw error;
    } finally {
      commit('setLoadingAvailability', false);
    }
  },
  
  async loadAvailabilitySlots({ commit, dispatch, getters, state }, params = {}) {
    // Save current slots before fetching (fetchAvailabilitySlots will replace them)
    const oldSlots = [...state.availabilitySlots];
    
    // Fetch new slots (this replaces state.availabilitySlots with newSlots)
    const newSlots = await dispatch('fetchAvailabilitySlots', params);
    
    // Handle case where fetchAvailabilitySlots returns undefined (e.g., if already loading)
    if (!newSlots || !Array.isArray(newSlots)) {
      return getters.availabilitySlots;
    }
    
    // Merge old slots back with new slots
    const allSlots = [...oldSlots, ...newSlots];
    commit('mergeAvailabilitySlots', allSlots);
    
    return getters.availabilitySlots;
  },
  
  async fetchResourceAvailability({ dispatch }, { resourceUids, ...options }) {
    return await dispatch('fetchAvailabilitySlots', {
      resource_uids: resourceUids.join(','),
      ...options,
    });
  },
  
  async fetchStaffAvailability({ dispatch }, { staffUids, ...options }) {
    return await dispatch('fetchAvailabilitySlots', {
      staff_uids: staffUids,
      ...options,
    });
  },
  
  async fetchResourceTypeAvailability({ dispatch }, { resourceTypeUids, ...options }) {
    return await dispatch('fetchAvailabilitySlots', {
      resource_type_uids: resourceTypeUids,
      ...options,
    });
  },
  
  async refreshAvailability({ commit, dispatch }, params = {}) {
    commit('resetAvailabilityData');
    return await dispatch('fetchAvailabilitySlots', params);
  },
  
  resetAvailabilityData({ commit }) {
    commit('resetAvailabilityData');
  },
  
  clearError({ commit }) {
    commit('clearError');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};